import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { 
  Return, 
  ReturnStatus, 
  ReturnReason, 
  ReturnFilters 
} from '../../models/return.models';
import { ReturnsService } from '../../services/returns.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-devoluciones',
  templateUrl: './devoluciones.component.html',
  styleUrls: ['./devoluciones.component.css']
})
export class DevolucionesComponent implements OnInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'returnNumber', 
    'client', 
    'originalVoucher', 
    'reason', 
    'status', 
    'totalAmount', 
    'requestedAt', 
    'actions'
  ];

  dataSource = new MatTableDataSource<Return>();
  isLoading = false;
  
  // Filtros
  filters: ReturnFilters = {
    page: 1,
    limit: 25
  };

  // Opciones para filtros
  statusOptions = Object.values(ReturnStatus);
  reasonOptions = Object.values(ReturnReason);

  // Traducciones para estados
  statusLabels = {
    [ReturnStatus.PENDING]: 'Pendiente',
    [ReturnStatus.APPROVED]: 'Aprobada',
    [ReturnStatus.REJECTED]: 'Rechazada',
    [ReturnStatus.PROCESSED]: 'Procesada'
  };

  // Traducciones para motivos
  reasonLabels = {
    [ReturnReason.DEFECTIVE]: 'Producto defectuoso',
    [ReturnReason.WRONG_ITEM]: 'Producto incorrecto',
    [ReturnReason.NOT_AS_DESCRIBED]: 'No coincide con descripción',
    [ReturnReason.CUSTOMER_CHANGED_MIND]: 'Cliente cambió de opinión',
    [ReturnReason.DAMAGED_IN_SHIPPING]: 'Dañado en envío',
    [ReturnReason.OTHER]: 'Otros'
  };

  constructor(
    private returnsService: ReturnsService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadReturns();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  /**
   * Carga las devoluciones aplicando filtros
   */
  loadReturns(): void {
    this.isLoading = true;
    
    this.returnsService.getReturns(this.filters).subscribe({
      next: (response) => {
        this.dataSource.data = response.data || response;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading returns:', error);
        Swal.fire('Error', 'Error al cargar las devoluciones', 'error');
        this.isLoading = false;
      }
    });
  }

  /**
   * Aplica filtros de búsqueda
   */
  applyFilters(): void {
    this.filters.page = 1; // Reset página
    this.loadReturns();
  }

  /**
   * Limpia todos los filtros
   */
  clearFilters(): void {
    this.filters = {
      page: 1,
      limit: 25
    };
    this.loadReturns();
  }

  /**
   * Navega a crear nueva devolución
   */
  createReturn(): void {
    this.router.navigate(['/devoluciones/crear']);
  }

  /**
   * Navega a ver detalle de devolución
   */
  viewReturn(returnItem: Return): void {
    this.router.navigate(['/devoluciones/detalle', returnItem.id]);
  }

  /**
   * Navega a editar devolución (solo si está pendiente)
   */
  editReturn(returnItem: Return): void {
    if (returnItem.status === ReturnStatus.PENDING) {
      this.router.navigate(['/devoluciones/editar', returnItem.id]);
    }
  }

  /**
   * Aprueba una devolución
   */
  approveReturn(returnItem: Return): void {
    Swal.fire({
      title: 'Aprobar Devolución',
      text: `¿Está seguro que desea aprobar la devolución ${returnItem.returnNumber}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Aprobar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.returnsService.approveReturn(returnItem.id).subscribe({
          next: () => {
            Swal.fire('Éxito', 'Devolución aprobada exitosamente', 'success');
            this.loadReturns();
          },
          error: (error) => {
            console.error('Error approving return:', error);
            Swal.fire('Error', 'Error al aprobar la devolución', 'error');
          }
        });
      }
    });
  }

  /**
   * Rechaza una devolución
   */
  rejectReturn(returnItem: Return): void {
    Swal.fire({
      title: 'Rechazar Devolución',
      text: `¿Está seguro que desea rechazar la devolución ${returnItem.returnNumber}?`,
      input: 'textarea',
      inputPlaceholder: 'Motivo del rechazo (opcional)',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.returnsService.rejectReturn(returnItem.id, result.value).subscribe({
          next: () => {
            Swal.fire('Rechazada', 'Devolución rechazada', 'success');
            this.loadReturns();
          },
          error: (error) => {
            console.error('Error rejecting return:', error);
            Swal.fire('Error', 'Error al rechazar la devolución', 'error');
          }
        });
      }
    });
  }

  /**
   * Procesa una devolución
   */
  processReturn(returnItem: Return): void {
    Swal.fire({
      title: 'Procesar Devolución',
      text: `¿Está seguro que desea procesar la devolución ${returnItem.returnNumber}? Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Procesar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.returnsService.processReturn(returnItem.id).subscribe({
          next: () => {
            Swal.fire('Éxito', 'Devolución procesada exitosamente', 'success');
            this.loadReturns();
          },
          error: (error) => {
            console.error('Error processing return:', error);
            Swal.fire('Error', 'Error al procesar la devolución', 'error');
          }
        });
      }
    });
  }

  /**
   * Elimina una devolución (solo si está pendiente)
   */
  deleteReturn(returnItem: Return): void {
    if (returnItem.status !== ReturnStatus.PENDING) {
      Swal.fire('Atención', 'Solo se pueden eliminar devoluciones pendientes', 'warning');
      return;
    }

    Swal.fire({
      title: 'Eliminar Devolución',
      text: `¿Está seguro que desea eliminar la devolución ${returnItem.returnNumber}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.returnsService.deleteReturn(returnItem.id).subscribe({
          next: () => {
            Swal.fire('Eliminada', 'Devolución eliminada exitosamente', 'success');
            this.loadReturns();
          },
          error: (error) => {
            console.error('Error deleting return:', error);
            Swal.fire('Error', 'Error al eliminar la devolución', 'error');
          }
        });
      }
    });
  }

  /**
   * Verifica si una acción está disponible según el estado
   */
  canPerformAction(returnItem: Return, action: string): boolean {
    switch (action) {
      case 'edit':
      case 'delete':
        return returnItem.status === ReturnStatus.PENDING;
      case 'approve':
      case 'reject':
        return returnItem.status === ReturnStatus.PENDING;
      case 'process':
        return returnItem.status === ReturnStatus.APPROVED;
      default:
        return false;
    }
  }

  /**
   * Obtiene la clase CSS para el estado
   */
  getStatusClass(status: ReturnStatus): string {
    switch (status) {
      case ReturnStatus.PENDING:
        return 'status-pending';
      case ReturnStatus.APPROVED:
        return 'status-approved';
      case ReturnStatus.REJECTED:
        return 'status-rejected';
      case ReturnStatus.PROCESSED:
        return 'status-processed';
      default:
        return '';
    }
  }
}